import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTheme } from '@/hooks/useTheme'
import { BaseView, BaseText } from '@/components/base'
import { ModalProps } from './types'
import useOffsets from '@/hooks/useOffsets'

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  showHandle = true,
  backdrop = true,
  className = '',
  style,
  useBottomOffset = false,
  enableDynamicSizing = true,
  snapPoints,
}) => {
  const theme = useTheme()
  const offsets = useOffsets()
  const [shouldRender, setShouldRender] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)

  // Управляем рендерингом для плавной анимации
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true)
      // Небольшая задержка для запуска анимации
      const animationTimer = setTimeout(() => setIsAnimating(true), 10)
      return () => clearTimeout(animationTimer)
    } else {
      setIsAnimating(false)
      // Задержка перед размонтированием для завершения анимации
      const timer = setTimeout(() => setShouldRender(false), 300)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  // Закрытие по Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      // Блокируем скролл body
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  // Определяем высоту модалки
  const getModalHeight = () => {
    // Если передан style.height, используем его
    if (style?.height) {
      return { height: style.height }
    }

    // Если enableDynamicSizing, модалка растягивается под контент
    if (enableDynamicSizing) {
      return {
        maxHeight: '90vh', // Ограничение чтобы не выйти за экран
      }
    }

    // Если есть snapPoints, используем первый
    if (snapPoints && snapPoints.length > 0) {
      return { height: snapPoints[0] }
    }

    // По умолчанию
    return { maxHeight: '80vh' }
  }

  const heightStyle = getModalHeight()

  if (!shouldRender) return null

  const modalContent = (
    <BaseView
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        opacity: isAnimating ? 1 : 0,
        pointerEvents: isOpen ? 'auto' : 'none',
        transition: 'opacity 0.3s ease-out',
      }}
    >
      {/* Backdrop - без blur */}
      {backdrop && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }}
          onClick={onClose}
        />
      )}

      {/* Modal Content - BottomSheet прижат к низу */}
      <BaseView
        className={className}
        style={{
          position: 'absolute',
          bottom: style?.top === 0 ? 0 : 0,
          top: style?.top === 0 ? 0 : undefined,
          left: 0,
          right: 0,
          width: '100%',
          ...heightStyle,
          backgroundColor: '#18181A',
          borderTopLeftRadius: style?.borderTopLeftRadius || 20,
          borderTopRightRadius: style?.borderTopRightRadius || 20,
          paddingTop: showHandle ? 12 : 16,
          paddingBottom: useBottomOffset ? 16 + offsets.bottom : 16,
          paddingLeft: 16,
          paddingRight: 16,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.25)',
          transform: isAnimating ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          ...style,
          // Перезаписываем height если нужно
          ...(style?.height ? {} : heightStyle),
        }}
      >
        {/* Handle - как в RN */}
        {showHandle && (
          <BaseView
            center
            style={{
              marginBottom: 30,
              paddingTop: 8,
              cursor: 'pointer',
            }}
            onClick={onClose}
          >
            <div
              style={{
                width: 30,
                height: 4,
                backgroundColor: theme.colors.label.disabled || '#666666',
                borderRadius: 2,
                opacity: 0.6,
              }}
            />
          </BaseView>
        )}

        {/* Title */}
        {title && (
          <BaseView center mb={20}>
            <BaseText
              fontSize={20}
              fontFamily="semibold"
              color="label.default"
              textAlign="center"
            >
              {title}
            </BaseText>
          </BaseView>
        )}

        {/* Content */}
        <BaseView
          style={{
            // При enableDynamicSizing убираем flex и overflow
            // чтобы контент растягивал модалку
            ...(enableDynamicSizing
              ? {
                  display: 'flex',
                  flexDirection: 'column',
                }
              : {
                  flex: 1,
                  overflowY: 'auto',
                  WebkitOverflowScrolling: 'touch',
                }),
          }}
        >
          {children}
        </BaseView>
      </BaseView>
    </BaseView>
  )

  // Рендерим в портал для правильного z-index
  return createPortal(modalContent, document.body)
}
